/**
 * Khung ung dung: noi loi, kho trang thai va cac lop hien thi lai voi nhau.
 *
 * App khong biet pdf.js ton tai. No chi thay giao dien PdfCore trong hop dong
 * dung chung. Khi ban WASM cua loi Rust san sang, tep nay khong doi mot dong -
 * TRU mot cho duy nhat da duoc dat sau mot cua so co ten: kha nang chay thu
 * mot lan sua (ui/textEditCore.ts), thu hop dong khong mo ta va giao dien buoc
 * phai co de canh bao TRUOC khi nguoi dung bam.
 *
 * DUONG DI CUA MOT LAN SUA CHU
 * ----------------------------
 *   bam vao khoi (cong cu "Sửa chữ")
 *     -> mo trinh soan tai cho, dien san van ban goc
 *     -> moi lan go: chay thu that qua loi, hien ket qua (co sua duoc khong,
 *        thieu glyph nao, co phai thay font khong)
 *     -> Enter: dispatch mot lenh text.replace vao kho trang thai
 *     -> lop chinh sua ve ngay ket qua len trang; Ctrl+Z lui lai duoc
 *     -> bam Luu: phat lai toan bo danh sach lenh len MOT BAN SAO cua byte goc
 *
 * Byte goc khong bao gio bi ghi vao o bat ky buoc nao (ADR-003).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { Annotation, EditCommand, PdfCore, PdfPoint, PdfRect, TextBlock } from '@pdfeditor/types';
import { getCore } from './core';
import { store, useAppState } from './state';
import { VirtualizedDocument } from './viewer/VirtualizedDocument';
import type { EditRenderHint } from './viewer/EditedTextLayer';
import { editedItemsFromCommands, hintKey } from './viewer/editedItems';
import type { EmbeddedImageBridge } from './viewer/PageView';
import { localMatrixForRect, currentFullMatrix, type ProjectedImage } from './core/content/projection';
import { Toolbar } from './ui/Toolbar';
import { PagePanel } from './ui/PagePanel';
import { usePageOps } from './ui/usePageOps';
import { TOOL_SHORTCUTS } from './ui/toolbarMenus';
import { DropZone } from './ui/DropZone';
import { baseNameOf, downloadPdfBytes } from './ui/download';
import { isPageCommand } from './core/pages/plan';
import { collapseImageEdits, isImageContentCommand } from './core/content/collapse';
import { asSourceViewCore } from './core/pages/capability';
import { resolvePlanView, planContainsOriginalPage } from './viewer/planView';
import { usePlanDocuments } from './viewer/usePlanDocuments';
import type { NoteDraft } from './viewer/PageView';
import type { TextBoxDraftStyle } from './viewer/NotePopup';
import { isAnnotationCommand } from './core/annotations/replay';
import { placeAnnotations } from './core/annotations/placement';
import { checkAnnotation, ANNOTATION_FIDELITY_NOTE } from './core/annotations/support';
import { rectChangePatch } from './core/annotations/shapes';
import {
  asImageCore,
  formatBytes as formatImageBytes,
  intakeImage,
  placementRect,
  type ImageAsset,
  type OversizeInfo,
} from './core/annotations/images';
import { InspectorPopover } from './ui/InspectorPopover';
import { PropertiesPanel, type PropertiesPanelProps } from './ui/PropertiesPanel';
import { EditFeedback } from './ui/EditFeedback';
import { KIND_LABEL } from './ui/objectLabels';
import { loadBundledFont, onBundledFontLoaded } from './core/fonts/bundled';
import type { Font } from './core/fonts/fontkit';
import {
  bundledFaces,
  DEFAULT_TEXT_BOX_WIDTH_PT,
  DEFAULT_TEXT_COLOR_RGB,
  DEFAULT_TEXT_SIZE_PT,
  NOTE_FILL_RGB,
  measureTextBox,
  reflowTextAnnotation,
  textBoxRect,
} from './ui/textBoxMetrics';
import { describeMissing } from './core/fonts/coverage';
import { familyFontSource } from './viewer/AnnotationLayer';
import {
  DEFAULT_FONT_FAMILY_ID,
  getFontFamily,
  normalizeFontFamilyId,
  type FontFamilyId,
} from './core/fonts/families';
import { nextZAbove, type ShapeTool } from './state';

import type { EditSession } from './ui/InlineTextEditor';
import { useTextEditPreview } from './ui/useTextEditPreview';
import {
  coreAcceptsAlign,
  hexColor,
  rgbFromHex,
  runsFontSizePt,
  type TextAlign,
} from './ui/textEditCore';
import { scopeAsBlock, type EditScope } from './core/edit/scope';

/**
 * Tran thoi gian cho mot lan xuat tep.
 *
 * 6 trang mat khoang 5 giay tren may thu, nen mot tai lieu vai tram trang co the
 * mat hang phut mot cach chinh dang. Nguong nay khong phai de bat cham - no de
 * bat TREO: mot loi hua khong bao gio giai quyet se de nut ket o "Đang xuất..."
 * vinh vien, va khong co duong nao thoat ra ngoai tai lai trang.
 */
const SAVE_TIMEOUT_MS = 180_000;

/**
 * KIEU CHU MAC DINH CUA MOT HOP CHU MOI.
 *
 * Bo gia tri nay di THANG vao o soan, va tu o soan di thang vao doi tuong duoc
 * tao ra. Mot bo mac dinh thu hai nam ben trong duong ghi la cach hai duong bat
 * dau tra loi khac nhau cho cung mot cau hoi "hop chu moi trong nhu the nao".
 */
const DEFAULT_DRAFT_STYLE: TextBoxDraftStyle = {
  fontSizePt: DEFAULT_TEXT_SIZE_PT,
  align: 'left',
  bold: false,
  italic: false,
  fontFamily: DEFAULT_FONT_FAMILY_ID,
  textColorRgb: DEFAULT_TEXT_COLOR_RGB,
};

/** Kieu chu THAT cua mot doi tuong chu, de mo lai o soan dung nhu no dang co. */
function draftStyleOf(a: Annotation): TextBoxDraftStyle {
  return {
    fontSizePt: a.fontSizePt ?? DEFAULT_TEXT_SIZE_PT,
    align: a.textAlign ?? 'left',
    bold: a.bold === true,
    italic: a.italic === true,
    fontFamily: normalizeFontFamilyId(a.fontFamily),
    textColorRgb: a.textColorRgb ?? DEFAULT_TEXT_COLOR_RGB,
  };
}

/**
 * Phim tat can le - CUNG bo phim ma InlineTextEditor dung (Ctrl+Shift+L/E/R).
 *
 * Co y lap lai bang anh xa nay thay vi xuat khau tu InlineTextEditor: hai noi
 * bat phim o hai tang khac nhau (mot trong o soan, mot toan cuc), va bang nay
 * la cho de ai doc App cung thay ngay chung phai giong nhau.
 */
const ALIGN_KEYS: Record<string, TextAlign> = { l: 'left', e: 'center', r: 'right' };

/**
 * Cho mot loi hua, nhung khong cho mai mai.
 *
 * Luu y: viec het gio KHONG huy duoc cong viec ben duoi - pdf-lib khong co
 * duong huy. No chi tra quyen dieu khien lai cho giao dien va bao cho nguoi
 * dung. Do la mot cai gia phai tra co y thuc, khong phai mot cho quen.
 */
function withTimeout<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const guard = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([work, guard]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * Mot phien soan. Giu ca PHAM VI chu khong chi ma khoi.
 *
 * Ly do: tren tai lieu nhieu bang, viec gom nhom keo ca bang vao mot khoi. Neu
 * phien soan chi biet den khoi thi moi lan sua deu la sua ca bang. Xem
 * core/edit/scope.ts.
 */
interface EditingState {
  scope: EditScope;
  /** Khoi cha - can de tinh lai pham vi khi noi rong / thu hep. */
  block: TextBlock;
  pageIndex: number;
  originalText: string;
  fontCount: number;
}

export function App() {
  const state = useAppState();
  const [core, setCore] = useState<PdfCore | null>(null);
  const [scrollRequest, setScrollRequest] = useState<{ pageIndex: number; nonce: number } | null>(
    null,
  );
  const nonceRef = useRef(0);
  const openTokenRef = useRef(0);

  // --- Trang thai soan thao ------------------------------------------------
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [draft, setDraft] = useState('');
  /**
   * Can le nguoi dung da CHON. null = chua chon, de loi suy ra tu chu cu.
   *
   * Khoi tao lai ve null moi lan doi pham vi: can le do duoc cua mot o gia va
   * cua ca bang la hai thu khac nhau, va giu lai lua chon cu qua mot lan doi
   * pham vi se am tham ap dat no len mot vung chua he duoc do.
   */
  const [align, setAlign] = useState<TextAlign | null>(null);
  /**
   * DAM / NGHIENG cho lan sua dang soan.
   *
   * Dat lai ve false moi lan doi pham vi, cung ly do voi `align`: hai o khac
   * nhau co the von da khac kieu chu, va giu lai lua chon cu se am tham in dam
   * mot vung nguoi dung chua he cham vao.
   *
   * KHONG phai trang thai trang tri: hai gia tri nay di vao lenh text.replace,
   * di vao lan chay thu, va quyet dinh mat chu duoc nhung vao tep. Xem hop dong
   * kieu va cua so tuoi moi trong InlineTextEditor.
   */
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  /**
   * PHONG CHU cho lan sua dang soan.
   *
   * Dat lai ve mac dinh moi lan doi pham vi, cung ly do voi `align` / `bold`:
   * moi pham vi la mot vung rieng, giu lai lua chon phong cu se am tham doi phong
   * mot vung nguoi dung chua cham vao. Di vao lenh text.replace va vao lan chay
   * thu, va quyet dinh ho font dong goi duoc nhung - khong phai trang thai trang
   * tri. Xem cua so tuoi moi trong ui/textEditorRules.ts.
   */
  const [family, setFamily] = useState<FontFamilyId>(DEFAULT_FONT_FAMILY_ID);
  /**
   * CO CHU nguoi dung chon cho lan sua chu san co, hoac null = giu co chu do duoc
   * tu chu cu.
   *
   * Dat lai ve null moi lan doi pham vi, cung ly do voi `align` / `bold` / phong:
   * moi o la mot vung rieng. GIOI HAN ADR-005: duong ghi de khong noi rong duoc
   * khoi, nen co chu lon hon cho co the bi thu nho lai de vua - bang phan hoi noi
   * ro. Di vao lenh text.replace va lan chay thu; khong phai trang thai trang tri.
   */
  const [fontSize, setFontSize] = useState<number | null>(null);

  /**
   * Anh CO SAN TRONG TAI LIEU dang duoc chon.
   *
   * Rieng biet voi `selectedAnnotationId`, va co y: hai thu la hai loai doi
   * tuong khac han nhau. Chu thich la thu TA tao ra va nam trong /Annots; anh
   * nay nam trong content stream cua chinh tai lieu goc, va moi thao tac tren
   * no la mot lan phau thuat vao noi dung do. Gop chung mot bien lua chon se
   * lam moi cho doc bien do phai hoi lai "cai nay thuoc loai nao".
   */
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  /**
   * BO NHO DEM so do dat chu (hop che, co chu, so dong, can le) lay tu bao cao
   * chay thu that cua loi. Chi phuc vu viec ve; su that van la danh sach lenh.
   *
   * KHOA THEO NOI DUNG LENH (hintKey), khong theo scope.id: mot o sua nhieu lan
   * cho nhieu so do khac nhau, va sau Ctrl+Z lop ve lui ve lan truoc thi phai
   * lay dung hint cua lan truoc. `activeHints` ben duoi doi chieu ve tung o dang
   * co hieu luc. Xem hintKey() va ghi chu o viewer/editedItems.ts.
   */
  const [hintCache, setHintCache] = useState<ReadonlyMap<string, EditRenderHint>>(new Map());
  const [saving, setSaving] = useState(false);

  /**
   * BYTE GOC, GIU NGUYEN VEN.
   *
   * Duong luu KHONG duoc phat lai danh sach lenh len chinh tai lieu dang xem:
   * bam Luu hai lan se ap dung moi lenh HAI LAN (chen chu hai lan, xoay 180 do
   * thay vi 90). Moi lan luu ta mo mot tai lieu MOI tu dung byte goc nay, phat
   * lai toan bo danh sach lenh len no, xuat byte, roi dong lai. Dung nghia
   * "tep goc la bat bien, chi vat chat hoa khi luu".
   */
  const originalBytesRef = useRef<Uint8Array | null>(null);

  /**
   * PHONG DONG GOI, nap mot lan cho ca phien.
   *
   * Giao dien can chinh doi tuong font nay chu khong chi byte cua no: moi hop
   * chu deu duoc DO bang fontkit truoc khi tao ra (ui/textBoxMetrics.ts). Nap
   * that bai thi cong cu chu bi khoa lai kem ly do - khong duoc doan bua mot
   * kich thuoc roi de nguoi dung phat hien ra sau khi xuat tep.
   */
  const [font, setFont] = useState<Font | null>(null);
  const [faceGeneration, setFaceGeneration] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void loadBundledFont().then((bundled) => {
      if (!cancelled) setFont(bundled?.font ?? null);
    });
    const off = onBundledFontLoaded(() => {
      if (!cancelled) setFaceGeneration((n) => n + 1);
    });
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * NGUON MAT CHU cho moi phep do o tang giao dien.
   *
   * Ban Regular la dieu kien can (khong co no thi khong do duoc gi), nhung phep
   * do phai chay tren dung mat chu cua doi tuong: ban Bold rong hon ban Regular,
   * nen mot hop do bang Regular roi ghi ra bang Bold se chat hon chinh chu ben
   * trong no. `faceGeneration` co trong danh sach phu thuoc de mot mat chu vua
   * tai xong lam sinh ra nguon moi - xem onBundledFontLoaded().
   */
  const faces = useMemo(
    () => (font ? bundledFaces(font) : null),
    [font, faceGeneration],
  );

  // Nap loi mot lan.
  useEffect(() => {
    let cancelled = false;
    void getCore().then((handle) => {
      if (cancelled) return;
      setCore(handle.core);
      store.setCore(handle.flavor, handle.label);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Loi co duong ghi hay khong.
   *
   * Truoc day cho nay chi chap nhan 'wasm', nen loi trinh duyet - vua sua chu
   * duoc that - van bi giao dien doi xu nhu ban chi doc. Do chinh la ly do
   * nguoi dung mo ung dung len va "chi xem duoc, khong sua duoc".
   */
  const writeCapable = state.coreFlavor === 'wasm' || state.coreFlavor === 'browser-pdflib';

  // --- Mo tep -------------------------------------------------------------

  const handleOpenFile = useCallback(
    async (file: File) => {
      const activeCore = core;
      if (!activeCore) return;
      if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
        store.notify('Chỉ mở được tệp PDF.', 'error');
        return;
      }

      const token = ++openTokenRef.current;
      store.beginLoad(file.name, file.size);
      setEditing(null);
      setDraft('');
      setAlign(null);
      setHintCache(new Map());
      setNoteDraft(null);
      setPendingImage(null);
      annotationNoticeShownRef.current = false;

      // Dong tai lieu cu truoc: moi tai lieu dang mo giu mot worker cua pdf.js.
      const previous = store.getSnapshot().doc;
      if (previous) void activeCore.close(previous);
      // ...VA ca cac tai lieu da ghep vao no. Chung khong thuoc so do trang moi
      // nen se khong bao gio duoc ve nua; giu chung mo la giu mot worker pdf.js
      // cho moi tep nguoi dung tung ghep, suot ca phien lam viec.
      void asSourceViewCore(activeCore)?.closeSourceViews();

      try {
        const buffer = await file.arrayBuffer();
        if (token !== openTokenRef.current) return;
        const bytes = new Uint8Array(buffer);

        originalBytesRef.current = bytes;

        // Truyen ban sao: mot so hien thuc loi (va pdf.js khi chuyen buffer
        // sang worker) chiem quyen so huu vung nho duoc truyen vao. Ban goc
        // trong originalBytesRef phai con nguyen cho duong luu.
        const opened = await activeCore.open(bytes.slice());
        if (token !== openTokenRef.current) return;
        if (!opened.ok) {
          store.failed(opened.error);
          return;
        }

        const info = await activeCore.info(opened.value);
        if (token !== openTokenRef.current) return;
        if (!info.ok) {
          store.failed(info.error);
          return;
        }

        store.loaded(opened.value, info.value);
      } catch (err) {
        store.failed({
          code: 'io-error',
          message: String(err),
          userMessage: 'Không đọc được tệp từ đĩa. Tệp có thể đã bị di chuyển hoặc khóa.',
        });
      }
    },
    [core],
  );

  // --- So do trang -> nhung gi khung xem ve --------------------------------

  /**
   * MOT PHEP TINH DUY NHAT CHO CA HAI KHUNG HINH.
   *
   * Thanh ben va khung xem chinh deu ve tu `viewSlots`, va `viewSlots` chi la
   * mot ham thuan tuy tren `state.pagePlan` - thu ma duong luu phat lai bang
   * dung ham do (replayPagePlan). Ba noi khong the noi ba dieu khac nhau ve
   * cung mot tai lieu nua.
   */
  const planDocuments = usePlanDocuments(core, state.doc, state.info, state.pagePlan);
  const viewSlots = useMemo(
    () => resolvePlanView(state.pagePlan, planDocuments),
    [state.pagePlan, planDocuments],
  );

  // --- Dieu huong ---------------------------------------------------------

  const goToPage = useCallback((pageIndex: number) => {
    nonceRef.current += 1;
    setScrollRequest({ pageIndex, nonce: nonceRef.current });
    store.setCurrentPage(pageIndex);
  }, []);

  const handleCurrentPageChange = useCallback((pageIndex: number) => {
    store.setCurrentPage(pageIndex);
  }, []);

  const handleBlocksLoaded = useCallback((pageIndex: number, blocks: TextBlock[]) => {
    store.setBlocksForPage(pageIndex, blocks);
  }, []);

  // --- Chu thich -----------------------------------------------------------

  /**
   * O chu giai cua ghi chu dinh dang mo. `id === null` la ghi chu chua tao.
   */
  const [noteDraft, setNoteDraft] = useState<NoteDraft | null>(null);
  /**
   * KIEU CHU cua hop chu / ghi chu dang soan.
   *
   * Truoc day song trong NotePopup. Da nang len App vi bang thuoc tinh o popup
   * (PropertiesPanel) sua no, con o go (NotePopup) chi ve theo no. Mot nguon duy
   * nhat cho ca noi sua lan noi ghi ra: khong co duong nao de kieu chu va noi
   * dung lech nhau.
   */
  const [noteStyle, setNoteStyle] = useState<TextBoxDraftStyle>(DEFAULT_DRAFT_STYLE);
  /** Da nhac ve gioi han cua chu thich cho tai lieu nay chua. */
  const annotationNoticeShownRef = useRef(false);

  /**
   * LOI VAO DUY NHAT de tao mot chu thich.
   *
   * Moi chu thich di qua checkAnnotation() TRUOC khi vao lich su - dung ham ma
   * duong ghi se goi lai o buoc luu. Neu bo cua kiem nay, mot dau mau gan trang
   * hay do mo bang 0 se duoc nhan vao lich su, hien tren man hinh (noi ta ve no
   * bang SVG voi luat khac), roi bi tu choi o buoc Luu - tuc la nguoi dung mat
   * viec dang lam vao dung luc ho tuong da xong.
   */
  const addAnnotation = useCallback((annotation: Annotation): boolean => {
    const checked = checkAnnotation(annotation, store.getSnapshot().info?.pageCount);
    if (!checked.ok) {
      store.notify(checked.reason, 'warn');
      return false;
    }
    store.dispatch({ kind: 'annotation.add', annotation });
    if (!annotationNoticeShownRef.current) {
      annotationNoticeShownRef.current = true;
      store.notify(ANNOTATION_FIDELITY_NOTE, 'info');
    }
    return true;
  }, []);

  const newAnnotationId = () =>
    `ann-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

  /**
   * ==========================================================================
   *  VUA TAO XONG MOT DOI TUONG -> NO DANG DUOC CHON, VA CONG CU DA HA XUONG
   * ==========================================================================
   *
   *  Truoc day chi rieng duong dat anh lam viec nay. Voi ghi chu, hop chu, hinh
   *  khoi va net ve tay, cong cu VAN CON BAT sau khi tao: cu bam tiep theo cua
   *  nguoi dung - cu bam ma ho dinh dung de tom lay doi tuong vua tao - lai tao
   *  ra mot doi tuong THU HAI. Chon, di chuyen va doi kich thuoc deu chay dung,
   *  chung chi khong bao gio VOI TOI DUOC.
   *
   *  Nen moi duong tao doi tuong ket thuc o day, va o day lam dung hai viec:
   *    - ha cong cu ve "Chọn", de cu bam ke tiep la mot lan chon chu khong phai
   *      mot lan tao;
   *    - chon chinh doi tuong vua tao, de tay nam hien ra ngay va no keo duoc
   *      ma khong can them mot thao tac nao.
   *
   *  KHONG di qua handleSelectAnnotation(): ham do con MO O SOAN cho doi tuong
   *  co chu. Voi mot ghi chu vua go xong, no se bat lai o soan ma nguoi dung
   *  vua dong - mot vong lap khong loi ra duoc.
   *
   *  KHONG CO PHIM BO TRO "GIU CONG CU". Da can nhac (nguoi dung dat lien mot
   *  loat hinh khoi se phai bam lai nut moi lan), va bo di: mot phim bo tro
   *  khong hien o dau tren giao dien la mot tinh nang khong ai tim thay, con
   *  neu no hien ra thi no la mot cong tac o thanh cong cu - ma thanh cong cu
   *  khong thuoc pham vi lan sua nay. Bam lai nut cong cu la MOT cu bam, va no
   *  hien ngay truoc mat. Con mot cong cu khong bao gio tu ha xuong thi khoa
   *  han doi tuong vua tao lai, va do la loi nguoi dung vua bao.
   */
  const finishCreation = useCallback((id: string) => {
    store.setTool('select');
    store.selectAnnotation(id);
  }, []);

  /**
   * Chon khoi van ban.
   *
   * Truoc day ham nay con mang mot vai tro thu hai: voi cong cu "Tô sáng", mot
   * lan chon la mot lan tao chu thich. Cong cu do da bi go bo theo yeu cau cua
   * nguoi dung, va duong di cua no duoc go SACH - khong con nut, khong con phim
   * tat, khong con nhanh nao o day sinh ra dau 'highlight'.
   */
  const handleSelectBlock = useCallback(
    (block: TextBlock | null, scope: EditScope | null) => {
      store.selectBlock(block, scope);
    },
    [],
  );

  /** Mot net ve tay da xong. Toa do da o he PDF va da duoc lam muot. */
  const handleInkStroke = useCallback(
    (pageIndex: number, path: PdfPoint[]) => {
      if (path.length < 2) return;
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      for (const p of path) {
        x0 = Math.min(x0, p.x);
        y0 = Math.min(y0, p.y);
        x1 = Math.max(x1, p.x);
        y1 = Math.max(y1, p.y);
      }
      const id = newAnnotationId();
      const added = addAnnotation({
        id,
        kind: 'ink',
        pageIndex,
        // Hop bao o day chi la gia tri khoi diem. Duong ghi tinh lai tu chinh
        // cac net (kem nua be rong net) - xem annotations/write.ts.
        rect: { x: x0, y: y0, width: x1 - x0, height: y1 - y0 },
        colorRgb: [0.88, 0.28, 0.24],
        opacity: 1,
        createdAtMs: Date.now(),
        inkPaths: [path],
      });
      // Ve xong mot net thi net do dang duoc chon va cong cu da ve "Chọn". Ve
      // lien mot loat net van lam duoc - bam lai nut "Vẽ tay" - nhung mac dinh
      // la net vua ve CAM DUOC ngay, vi do la thu nguoi dung thuong muon lam
      // ke tiep: dat lai cho no cho dung.
      if (added) finishCreation(id);
    },
    [addAnnotation, finishCreation],
  );

  /** Bam dat mot ghi chu moi: mo o soan, chua tao gi ca. */
  const handleNotePoint = useCallback((pageIndex: number, point: PdfPoint) => {
    const kind = store.getSnapshot().tool === 'freetext' ? 'free-text' : 'text-note';
    // Doi tuong chua ton tai nen chua co kieu chu nao de lay: mo o soan voi bo
    // mac dinh, va nguoi dung doi ngay trong luc go (qua bang thuoc tinh o popup).
    setNoteDraft({ id: null, kind, pageIndex, point, value: '', style: DEFAULT_DRAFT_STYLE });
    setNoteStyle(DEFAULT_DRAFT_STYLE);
  }, []);

  /**
   * Bam vao mot dau da co.
   *
   * Luon CHON no (thanh thuoc tinh can biet doi tuong nao dang duoc chon), va
   * neu do la doi tuong co chu thi mo luon o soan - mot thao tac thay vi hai.
   */
  const handleSelectAnnotation = useCallback((id: string) => {
    const found = store.getSnapshot().annotations.find((a) => a.id === id);
    if (!found) return;
    store.selectAnnotation(id);
    if (found.kind !== 'text-note' && found.kind !== 'free-text') return;
    const style = draftStyleOf(found);
    setNoteDraft({
      id: found.id,
      kind: found.kind,
      pageIndex: found.pageIndex,
      point: { x: found.rect.x, y: found.rect.y + found.rect.height },
      value: found.contents ?? '',
      // KIEU CHU THAT CUA DOI TUONG, khong phai mac dinh: mo lai mot hop dam,
      // can giua, 24pt roi thay o soan hien 12pt can trai thi mot cu bam Lưu se
      // lang le tra no ve mac dinh.
      style,
    });
    setNoteStyle(style);
  }, []);

  /**
   * ==========================================================================
   *  GHI CHU: MOT HOP DOC DUOC, KHONG PHAI MOT O VUONG NHO
   * ==========================================================================
   *
   *  Truoc day mot ghi chu duoc ghi ra la chu thich /Text theo dac ta: mot
   *  BIEU TUONG 20x20 diem, noi dung chi hien khi ro chuot hoac mo bang chu
   *  thich. Do la PDF dung chuan - va nguoi dung mo tep ra chi thay "hình
   *  vuông nhỏ màu vàng", roi bao la tinh nang bi hong.
   *
   *  Ho khong sai. Mot ghi chu khong ai doc duoc la mot ghi chu khong ai dung.
   *  Nen MAC DINH bay gio la 'callout': mot hop giay vang co chu ben trong,
   *  doc duoc ngay tren trang va ke ca khi in ra. Hanh vi cu cua Acrobat van
   *  con - doi `noteStyle` sang 'icon' - no chi khong con la mac dinh.
   *
   *  KICH THUOC HOP do tu MUC THAT cua glyph qua fontkit, khong tu co chu.
   *  Nguyen am tieng Viet chong hai tang dau vuon 1.048em tren duong co so, gan
   *  gap doi chu thuong ASCII (0.546em); mot hop tinh theo co chu se cat cut
   *  dau cua "Ề", "Ữ". Xem ui/textBoxMetrics.ts.
   */
  const handleNoteSave = useCallback(
    (text: string) => {
      const draft = noteDraft;
      if (!draft) return;
      // KIEU CHU lay tu state noteStyle (do bang thuoc tinh o popup sua), khong
      // tu doi tuong: nguoi dung vua chon dam, co chu, can le trong luc go.
      const style = noteStyle;

      const trimmed = text.trim();
      if (trimmed.length === 0) {
        // Mot hop rong ghi ra la mot hinh chu nhat khong noi gi. Neu dang sua
        // mot hop da co thi day chinh la y muon xoa no.
        if (draft.id !== null) store.dispatch({ kind: 'annotation.remove', id: draft.id });
        setNoteDraft(null);
        return;
      }

      if (!faces) {
        store.notify(
          'Chưa nạp được phông Noto Sans nên chưa đo được hộp chữ. Thử lại sau một nhịp.',
          'warn',
        );
        return;
      }

      // CUA CHAN GLYPH, TRUOC KHI GHI - khong phai sau. Kiem "chuoi trich xuat
      // ra co dung khong" khong chung minh duoc gi: /ToUnicode va glyph duoc ve
      // la hai thu doc lap. Chi doi chieu ma Unicode voi chuong trinh font that
      // moi tra loi duoc, va do la viec checkCoverage() lam.
      const existing =
        draft.id !== null
          ? store.getSnapshot().annotations.find((a) => a.id === draft.id)
          : undefined;
      // KIEU CHU DEN TU O SOAN, khong tu doi tuong. Nguoi dung vua chon dam, co
      // chu va can le NGAY TRONG LUC GO; doc lai tu doi tuong o day se vut bo
      // dung nhung lua chon vua roi va ghi ra ban cu.
      const fontSizePt = style.fontSizePt;
      const textAlign = style.align;
      const widthPt = existing?.rect.width ?? DEFAULT_TEXT_BOX_WIDTH_PT;

      const bold = style.bold;
      const italic = style.italic;
      const fontFamily = style.fontFamily;

      // Do bang DUNG ho font se duoc nhung, khong phai luon Noto Sans: moi ho co
      // be rong glyph rieng va do phu glyph rieng (bai hoc so 4 va so 6). Lui ve
      // `faces` mac dinh chi khi ho duoc chon chua nap xong.
      const source = familyFontSource(faces, fontFamily) ?? faces;

      const metrics = measureTextBox(source, trimmed, {
        fontSizePt,
        align: textAlign,
        widthPt,
        bold,
        italic,
      });
      if (!metrics.coverage.ok) {
        store.notify(
          `Phông ${getFontFamily(fontFamily).label} thiếu glyph cho: ` +
            `${describeMissing(metrics.coverage.missing)}. ` +
            'Ghi ra bây giờ sẽ cho ô vuông trống trên trang.',
          'error',
        );
        return;
      }

      if (draft.id === null) {
        const anchor = draft.point;
        const id = newAnnotationId();
        const added = addAnnotation({
          id,
          kind: draft.kind,
          pageIndex: draft.pageIndex,
          // Diem bam la goc TREN-TRAI. Chu moc XUONG tu do, nhu moi trinh soan
          // thao khac; neo goc duoi thi hop boc len che mat dong vua bam vao.
          rect: textBoxRect(anchor, metrics),
          // Ghi chu: giay vang co dien, co ca vien lan nen. Hop chu tu do:
          // khong to nen (null = KHONG TO, khac voi "chua chon"), de chu nam
          // truc tiep tren trang y nhu cong cu "text" cua ilovepdf.
          colorRgb: draft.kind === 'text-note' ? NOTE_FILL_RGB : [0.2, 0.35, 0.75],
          fillRgb: draft.kind === 'text-note' ? NOTE_FILL_RGB : null,
          textColorRgb: [...style.textColorRgb] as [number, number, number],
          opacity: 1,
          contents: trimmed,
          fontSizePt,
          textAlign,
          ...(bold ? { bold: true } : {}),
          ...(italic ? { italic: true } : {}),
          // Phong chu chi ghi khi KHAC mac dinh: hop chu cu khong mang truong nay
          // van doc lai duoc bang noto-sans.
          ...(fontFamily !== DEFAULT_FONT_FAMILY_ID ? { fontFamily } : {}),
          noteStyle: 'callout',
          z: nextZAbove(store.getSnapshot().annotations),
          createdAtMs: Date.now(),
        });
        // DUNG THOI DIEM: doi tuong chu sinh ra o day, luc nguoi dung bam "Lưu"
        // trong o soan - khong phai o cu bam dat diem ban dau. Ha cong cu som
        // hon se lam cu bam dau tien chi mo o soan roi khong tao ra gi.
        //
        // setNoteDraft(null) o cuoi ham dong o soan lai; chon doi tuong bang
        // store.selectAnnotation (chu khong phai handleSelectAnnotation) nen o
        // soan KHONG bat lai. Hop vua go xong hien tay nam va keo duoc ngay.
        if (added) finishCreation(id);
      } else {
        // Doi chu thi hop phai DOI THEO. Bo qua buoc nay se de lai mot hop cao
        // bang noi dung cu: hoac cat cut chu moi, hoac de thua mot mang trong.
        // Ca noi dung LAN kieu chu di trong MOT lenh, va `rect` la hop vua do
        // lai cho dung bo gia tri do. Ghi noi dung o day roi de kieu chu cho
        // thanh thuoc tinh lam sau se cho ra mot hop do theo mat chu nay ma ve
        // bang mat chu kia - dong cuoi nam ngoai hop, tren man hinh lan trong
        // tep.
        store.dispatch({
          kind: 'annotation.update',
          id: draft.id,
          patch: {
            contents: trimmed,
            rect: textBoxRect(draft.point, metrics),
            fontSizePt,
            textAlign,
            bold,
            italic,
            fontFamily,
            textColorRgb: [...style.textColorRgb] as [number, number, number],
          },
        });
      }
      setNoteDraft(null);
    },
    [noteDraft, noteStyle, addAnnotation, faces, finishCreation],
  );

  // --- Doi tuong dang chon: thanh thuoc tinh & bang doi tuong ---------------

  const selectedAnnotation = useMemo(
    () => state.annotations.find((a) => a.id === state.selectedAnnotationId) ?? null,
    [state.annotations, state.selectedAnnotationId],
  );

  /**
   * Ap mot thay doi thuoc tinh len doi tuong dang chon.
   *
   * Moi thay doi cham toi kieu chu deu keo theo mot lan DO LAI HOP, va phep do
   * do di qua dung ham reflowTextAnnotation() ma duong tao moi cung dung. Neu
   * o day tu tinh chieu cao rieng thi doi co chu tu 12 len 24 se cho ra mot hop
   * van cao nhu cu, va chu bi cat mat mot nua.
   */
  const patchAnnotation = useCallback(
    (patch: Partial<Annotation>) => {
      const target = selectedAnnotation;
      if (!target) return;

      // DAM VA NGHIENG CO TRONG DANH SACH NAY. Chung doi MAT CHU, va mat chu
      // doi thi be rong tung chu doi theo: ban Bold rong hon ban Regular, nen
      // mot dong vua khit truoc do se tran ra mot dong nua. Bo qua thi hop giu
      // chieu cao cu va dong tran nam ngoai hop - tren man hinh lan trong tep.
      // PHONG CHU CO TRONG DANH SACH NAY. Doi ho font doi be rong tung glyph -
      // Noto Serif rong khac Noto Sans - nen hop phai duoc DO LAI theo phong moi,
      // neu khong dong cuoi tran ra ngoai hop tren man hinh lan trong tep.
      const touchesLayout =
        patch.fontSizePt !== undefined ||
        patch.textAlign !== undefined ||
        patch.contents !== undefined ||
        patch.bold !== undefined ||
        patch.italic !== undefined ||
        patch.fontFamily !== undefined;
      const isTextObject = target.kind === 'free-text' || target.kind === 'text-note';

      let full: Partial<Annotation> = patch;
      if (isTextObject && touchesLayout && faces) {
        // Do lai bang DUNG ho font ket qua (phong moi neu patch doi phong, khong
        // thi phong dang co cua doi tuong). Ho font di qua NGUON MAT CHU, khong
        // qua tham so cua reflow - xem familyFontSource().
        const effectiveFamily = normalizeFontFamilyId(patch.fontFamily ?? target.fontFamily);
        const source = familyFontSource(faces, effectiveFamily) ?? faces;
        const { rect } = reflowTextAnnotation(source, target, {
          ...(patch.contents !== undefined ? { contents: patch.contents } : {}),
          ...(patch.fontSizePt !== undefined ? { fontSizePt: patch.fontSizePt } : {}),
          ...(patch.textAlign !== undefined ? { textAlign: patch.textAlign } : {}),
          ...(patch.bold !== undefined ? { bold: patch.bold } : {}),
          ...(patch.italic !== undefined ? { italic: patch.italic } : {}),
        });
        full = { ...patch, rect };
      }

      // Di qua checkAnnotation TRUOC khi vao lich su, y nhu duong tao moi: mot
      // do mo 0 hay mot mau gan trang phai bi tu choi o day chu khong o buoc
      // Luu, luc nguoi dung tuong minh da xong viec.
      const preview: Annotation = { ...target, ...full };
      const checked = checkAnnotation(preview, store.getSnapshot().info?.pageCount);
      if (!checked.ok) {
        store.notify(checked.reason, 'warn');
        return;
      }
      store.dispatch({ kind: 'annotation.update', id: target.id, patch: full });
    },
    [selectedAnnotation, faces],
  );

  /**
   * ==========================================================================
   *  KEO DI CHO / DOI KICH THUOC -> MOT LENH, KHONG PHAI TRANG THAI MAN HINH
   * ==========================================================================
   *
   *  `rect` den day DA XONG: lop chu thich tinh no bang resolveResize() /
   *  moveRect() cua core/annotations/shapes.ts - dung cac ham ma o chon vua ve
   *  ban xem truoc bang, va voi hop chu thi da do lai chieu cao qua
   *  layoutTextAnnotation(). O day KHONG duoc tinh lai gi ca; moi phep tinh
   *  them o cho nay la mot ban thu hai se troi khoi ban kia.
   *
   *  Va no di qua DANH SACH LENH (ADR-003) chu khong phai mot o useState nao
   *  do. Mot lan doi kich thuoc chi song tren man hinh se: khong hoan tac duoc,
   *  va bien mat khi xuat tep - dung loai loi ma du an nay cu tim thay mai.
   */
  const changeAnnotationRect = useCallback((id: string, rect: PdfRect) => {
    const target = store.getSnapshot().annotations.find((a) => a.id === id);
    if (!target) return;
    // NET VE TAY can nhieu hon mot `rect`: hinh dang cua no nam trong
    // `inkPaths`, va ca lop hien thi lan /InkList deu doc tu do. Mot lenh chi
    // doi `rect` se cho ra mot o chon nhay sang cho moi trong khi net van nam
    // nguyen cho cu. rectChangePatch() dich `rect` moi thanh phep bien doi that
    // tren cac diem - va no la MOT ham, dung o day va o cua kiem ben duoi.
    const patch = rectChangePatch(target, rect);
    // Cung cua kiem ma duong tao moi di qua: mot doi tuong bi keo ra ngoai mep
    // giay hay bop den kich thuoc vo nghia phai bi tu choi TAI DAY, khong phai
    // o buoc Luu luc nguoi dung tuong minh da xong viec.
    const checked = checkAnnotation({ ...target, ...patch }, store.getSnapshot().info?.pageCount);
    if (!checked.ok) {
      store.notify(checked.reason, 'warn');
      return;
    }
    store.dispatch({ kind: 'annotation.update', id, patch });
  }, []);

  const reorderAnnotation = useCallback((id: string, direction: 'up' | 'down') => {
    const command = store.reorderCommand(id, direction);
    if (command) store.dispatch(command);
  }, []);

  // Di qua store.removeAnnotation() chu khong dispatch thang: mot phim Delete
  // di qua hai bo nghe phim khac nhau va ca hai deu goi vao day. Xem giai thich
  // day du o state/store.ts.
  const deleteAnnotation = useCallback((id: string) => {
    store.removeAnnotation(id);
  }, []);

  /**
   * Ve xong mot hinh khoi bang thao tac keo. Toa do da o he PDF.
   *
   * Hinh qua nho bi BO chu khong ghi: mot cu bam le tay tren trang khong duoc
   * de lai mot dau 1x1 diem ma nguoi dung khong nhin thay va khong bam trung
   * duoc de xoa.
   */
  const handleShape = useCallback(
    (tool: ShapeTool, pageIndex: number, rect: PdfRect) => {
      if (rect.width < 2 && rect.height < 2) return;
      const id = newAnnotationId();
      const added = addAnnotation({
        id,
        kind: tool,
        pageIndex,
        rect,
        colorRgb: [0.88, 0.28, 0.24],
        // Khung va hinh bau duc mac dinh KHONG to: nguoi dung khoanh mot vung
        // de chi vao noi dung ben duoi, va to kin no lai se che mat chinh cai
        // ho dang chi. `null` la "khong to" tuong minh - xem hop dong kieu.
        fillRgb: null,
        opacity: 1,
        z: nextZAbove(store.getSnapshot().annotations),
        createdAtMs: Date.now(),
      });
      // Ve xong mot hinh thi hinh do dang duoc chon: keo goc de sua lai kich
      // thuoc, hoac keo than de dat lai cho, deu lam duoc ngay. Muon ve tiep
      // mot hinh nua thi bam lai nut cong cu - mot cu bam, va no hien ra day.
      if (added) finishCreation(id);
    },
    [addAnnotation, finishCreation],
  );

  // --- Chen anh -------------------------------------------------------------

  /**
   * ==========================================================================
   *  ANH: TU MOT TEP TREN DIA DEN MOT /Stamp CO DONG HINH DANG
   * ==========================================================================
   *
   *  DUONG DI DAY DU, va tung buoc deu co mot ly do:
   *
   *    bam "Chèn ảnh"  -> hop thoai chon tep (PNG / JPEG)
   *    -> intakeImage() GIAI MA THAT bang chinh bo giai ma cua duong ghi. Mot
   *       tep .txt doi ten thanh .png bi tu choi O DAY, kem ly do tieng Viet -
   *       khong phai o giua duong luu.
   *    -> anh qua lon: HOI truoc khi giam mau, va NOI RO da giam bao nhieu.
   *    -> ghi nhan vao ImageStore cua loi, nhan ve mot MA tra cuu.
   *    -> nguoi dung keo mot khung (hoac bam mot cai) tren trang.
   *    -> mot lenh annotation.add mang kind 'stamp' + `imageRef` = ma do.
   *    -> luc Luu, writeAnnotations() tra cuu ma trong ImageStore de lay byte.
   *
   *  BYTE ANH KHONG BAO GIO DI VAO DANH SACH LENH. Xem ghi chu `imageRef` trong
   *  hop dong kieu: mot lenh mang vai megabyte PNG se lam moi buoc hoan tac,
   *  moi lan gop lenh va (sau nay) moi goi tin cong tac phinh len theo.
   */

  /** Anh da ghi nhan, dang cho nguoi dung dat len trang. */
  const [pendingImage, setPendingImage] = useState<ImageAsset | null>(null);

  /**
   * DIA CHI HIEN THI cua tung anh, tao mot lan va giu lai.
   *
   * `URL.createObjectURL` cap phat mot muc trong bang cua tai lieu; tao lai moi
   * lan ve se ro ri mot muc cho MOI khung hinh trong khi keo. Nen ban do nay
   * ton tai, va no chi bi don khi doi tep - dung luc cac dia chi do thoi co
   * nguoi dung.
   *
   * KHONG xoa muc khi hoan tac. Ctrl+Z bo mot lenh khoi tien to, roi Ctrl+Shift+Z
   * mang no tro lai voi DUNG `imageRef` cu; xoa dia chi o buoc hoan tac se lam
   * lan lam lai ve ra mot o gach dut thay vi buc anh.
   */
  const imageUrlsRef = useRef(new Map<string, string>());

  const imageUrlFor = useCallback(
    (ref: string): string | undefined => {
      const cached = imageUrlsRef.current.get(ref);
      if (cached) return cached;
      const asset = asImageCore(core)?.imageAsset(ref);
      if (!asset) return undefined;
      const url = URL.createObjectURL(
        new Blob([asset.bytes.slice()], {
          type: asset.format === 'png' ? 'image/png' : 'image/jpeg',
        }),
      );
      imageUrlsRef.current.set(ref, url);
      return url;
    },
    [core],
  );

  // Don dia chi khi doi tai lieu hoac roi khoi ung dung. Bo qua buoc nay thi
  // moi anh tung chen trong ca phien lam viec deu con nam trong bo nho.
  useEffect(() => {
    const urls = imageUrlsRef.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, [state.doc]);

  /**
   * PHIM SHIFT DANG GIU HAY KHONG.
   *
   * Doc tu su kien con tro chu khong chi tu ban phim: `pointermove` mang co
   * `shiftKey` va no chay LIEN TUC trong suot thao tac keo, nen gia tri o day
   * luon dung tai dung thoi diem nguoi dung tha tay.
   *
   * DAY LA MOT CHO VA. Dung ra `onImageRect` cua AnnotationLayer nen mang theo
   * `keepAspect` y nhu `onShape` mang `constrain` - luc do bien nay bien mat.
   * Xem ghi chu ban giao trong bao cao: day la thu can o AnnotationLayer.
   */
  const shiftRef = useRef(false);
  useEffect(() => {
    const track = (e: PointerEvent | KeyboardEvent) => {
      shiftRef.current = e.shiftKey;
    };
    const opts = { capture: true, passive: true } as const;
    window.addEventListener('pointermove', track as EventListener, opts);
    window.addEventListener('pointerdown', track as EventListener, opts);
    window.addEventListener('keydown', track as EventListener, opts);
    window.addEventListener('keyup', track as EventListener, opts);
    return () => {
      window.removeEventListener('pointermove', track as EventListener, opts);
      window.removeEventListener('pointerdown', track as EventListener, opts);
      window.removeEventListener('keydown', track as EventListener, opts);
      window.removeEventListener('keyup', track as EventListener, opts);
    };
  }, []);

  /**
   * Cau hoi truoc khi giam mau. `window.confirm` chu khong phai mot toast:
   * day la mot lan SUA DU LIEU cua nguoi dung, va no phai chan lai cho den khi
   * co cau tra loi. Mot thong bao thoang qua se bi bo lo, va bo lo o day nghia
   * la hoac tep phinh len ba chuc megabyte, hoac anh bi doi kich thuoc ma chu
   * nhan khong he dong y.
   */
  const confirmDownsample = useCallback((info: OversizeInfo, label: string): boolean => {
    const mp = (info.pixels / 1_000_000).toFixed(1).replace('.', ',');
    return window.confirm(
      `Ảnh “${label}” là ${info.widthPx}×${info.heightPx} (${mp} triệu điểm ảnh).\n\n` +
        `Nhúng nguyên kích thước đó sẽ làm tệp PDF nặng lên rất nhiều mà KHÔNG hiện thêm ` +
        `chi tiết nào trên giấy: một trang A4 in ở 300 dpi chỉ chứa được khoảng 8,7 triệu ` +
        `điểm ảnh.\n\n` +
        `Giảm xuống ${info.targetWidthPx}×${info.targetHeightPx} rồi chèn?\n\n` +
        `Tệp ảnh gốc trên đĩa của bạn KHÔNG bị sửa.`,
    );
  }, []);

  const handlePickImage = useCallback(
    async (file: File) => {
      const imageCore = asImageCore(core);
      if (!imageCore) {
        store.notify('Lõi hiện tại không nhúng được ảnh.', 'warn');
        return;
      }

      let bytes: Uint8Array;
      try {
        bytes = new Uint8Array(await file.arrayBuffer());
      } catch (err) {
        store.notify(`Không đọc được tệp ảnh: ${String(err).slice(0, 120)}`, 'error');
        return;
      }

      const outcome = await intakeImage(bytes, file.name, {
        register: (b, l) => imageCore.registerImage(b, l),
        confirmDownsample,
      });
      if (!outcome.ok) {
        // `reason === null` la nguoi dung tu huy o hop thoai giam mau. Bao no ra
        // nhu mot that bai se noi voi ho rang lua chon cua ho la mot loi.
        if (outcome.reason) store.notify(outcome.reason, 'warn');
        return;
      }

      // Tao san dia chi hien thi: den luc lop chu thich hoi thi no phai co ngay,
      // khong duoc de mot khung hinh ve ra o gach dut roi moi thay anh.
      imageUrlFor(outcome.asset.ref);
      setPendingImage(outcome.asset);
      store.setTool('image');

      const guide =
        'Kéo một khung trên trang để đặt ảnh, hoặc bấm một cái để đặt ở cỡ tự nhiên. ' +
        'Ảnh giữ đúng tỉ lệ; giữ Shift khi thả để kéo giãn tự do.';
      // MOT thong bao duy nhat: notify() chi hien mot cai mot luc, nen phat ba
      // cai lien tiep se lam hai cai dau bien mat truoc khi doc kip.
      store.notify(
        [...outcome.notices, guide].join(' '),
        outcome.notices.length > 0 ? 'warn' : 'info',
      );
    },
    [core, confirmDownsample, imageUrlFor],
  );

  /**
   * Nguoi dung da chi cho dat anh.
   *
   * Hinh chu nhat cuoi cung do placementRect() tinh - DUNG ham do, va chi mot
   * lan. Lop hien thi ve buc anh tu `annotation.rect`, con duong ghi dung chinh
   * `annotation.rect` lam /Rect va /BBox cua dong hinh dang. Ban xem truoc va
   * tep xuat ra khong con cho nao de troi khoi nhau.
   */
  const handleImageRect = useCallback(
    (pageIndex: number, box: PdfRect | null, point: PdfPoint) => {
      const asset = pendingImage;
      if (!asset) {
        store.notify('Chưa chọn ảnh nào — bấm “Chèn ảnh” để chọn tệp.', 'warn');
        return;
      }
      const rect = placementRect(asset, point, box, !shiftRef.current);
      const id = newAnnotationId();
      const placed = addAnnotation({
        id,
        kind: 'stamp',
        pageIndex,
        rect,
        // Anh mang mau cua chinh no; `colorRgb` cua mot /Stamp khong ve gi ca.
        // checkAnnotation() mien cua chan mau cho rieng loai nay - xem support.ts.
        colorRgb: [0, 0, 0],
        opacity: 1,
        imageRef: asset.ref,
        z: nextZAbove(store.getSnapshot().annotations),
        createdAtMs: Date.now(),
      });
      if (!placed) return;

      // Dat xong thi HET: cong cu quay ve "Chọn" va anh cho san duoc go xuong.
      // Giu no lai se lam cu bam tiep theo tren trang de ra mot ban sao thu hai
      // cua cung buc anh, va nguoi dung khong he yeu cau dieu do.
      setPendingImage(null);
      // Duong nay von la duong duy nhat ha cong cu xuong. No khong con dac
      // biet: moi duong tao doi tuong deu ket thuc o finishCreation(), va o
      // day no con them mot viec ma truoc kia thieu - CHON buc anh vua dat,
      // de tay nam hien ra ngay thay vi doi mot cu bam nua.
      finishCreation(id);
      store.notify(
        `Đã đặt ảnh “${asset.label}” ở khung ${Math.round(rect.width)}×${Math.round(rect.height)} điểm. ` +
          `Tệp xuất ra sẽ nặng thêm khoảng ${formatImageBytes(asset.embeddedBytes)}.`,
        'info',
      );
    },
    [pendingImage, addAnnotation, finishCreation],
  );

  // Doi sang cong cu khac thi bo anh dang cho, y nhu o chu giai. Mot buc anh
  // "dang cho dat" ma cong cu da doi la mot cai bay: nut Chèn ảnh con sang len
  // trong khi khong cu bam nao con dat duoc no.
  useEffect(() => {
    if (state.tool !== 'image' && pendingImage) setPendingImage(null);
  }, [state.tool, pendingImage]);

  const handleNoteDelete = useCallback(() => {
    if (noteDraft?.id) store.dispatch({ kind: 'annotation.remove', id: noteDraft.id });
    setNoteDraft(null);
  }, [noteDraft]);

  const handleNoteCancel = useCallback(() => setNoteDraft(null), []);

  /**
   * Ap mot thay doi kieu chu cho hop chu / ghi chu dang soan.
   *
   * Bang thuoc tinh o popup goi vao day. Chi doi state noteStyle - noi dung se
   * duoc ghi cung mot bo gia tri khi bam Lưu (handleNoteSave doc noteStyle).
   */
  const patchNoteStyle = useCallback((patch: Partial<TextBoxDraftStyle>) => {
    setNoteStyle((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleNoteAlignChange = useCallback(
    (align: TextAlign) => setNoteStyle((prev) => ({ ...prev, align })),
    [],
  );

  // --- Soan thao van ban tai cho ------------------------------------------

  const closeEditor = useCallback(() => {
    setEditing(null);
    setDraft('');
    setAlign(null);
    setBold(false);
    setItalic(false);
    setFamily(DEFAULT_FONT_FAMILY_ID);
    setFontSize(null);
  }, []);

  /**
   * Mo (hoac chuyen) phien soan sang mot pham vi.
   *
   * Dung chung cho ca "bam vao trang" va "noi rong / thu hep pham vi": ca hai
   * deu la cung mot viec - dat lai phien soan len mot don vi khac va dien lai o
   * soan bang van ban DANG CO HIEU LUC cua don vi do.
   */
  const openScope = useCallback(
    (block: TextBlock, scope: EditScope) => {
      if (!writeCapable) {
        store.notify('Lõi hiện tại chỉ đọc — chưa sửa được văn bản.', 'warn');
        return;
      }
      if (editing?.scope.id === scope.id) return;

      // Bo mot ban nhap chua ap dung thi phai NOI RA. Mat chu ma khong ai bao
      // la kieu hong nguoi dung khong bao gio tha thu. Doi pham vi cung tinh:
      // no vut di dung nhung gi vua go.
      if (editing && draft !== editing.originalText) {
        store.notify('Đã bỏ nội dung chưa áp dụng ở phần trước.', 'warn');
      }

      // Van ban khoi diem la ban DANG CO HIEU LUC: neu pham vi nay da tung duoc
      // sua thi phai dien ban da sua, khong phai ban goc trong tep.
      const current = store.getSnapshot().editedText.get(scope.id) ?? scope.text;
      setEditing({
        scope,
        block,
        pageIndex: scope.pageIndex,
        // So font lay tu CHINH pham vi, khong tu khoi cha: canh bao "khối này
        // dùng 5 font" khi nguoi dung chi sua mot o dung mot font la noi sai.
        fontCount: scopeAsBlock(block, scope).fonts.length,
        originalText: current,
      });
      setDraft(current);
      // Ve "theo tai lieu" moi lan doi pham vi - xem ghi chu o cho khai bao.
      setAlign(null);
      setBold(false);
      setItalic(false);
      setFamily(DEFAULT_FONT_FAMILY_ID);
      setFontSize(null);
      store.selectBlock(block, scope);
      // MO O SOAN = MO BANG THONG TIN, luon luon. Nguoi dung bao: "khi focus vào
      // text area, popup thông tin nổi lên để setup tùy chọn cho ô đó". Truoc day
      // chi dua vao effect doi-muc-tieu (mo khi propTarget doi) - nhung no khong
      // bat lai khi bam lai dung o vua dong bang, va co the truot trong vai chuoi
      // thao tac. Mo thang o day thi moi lan vao mot o de sua deu chac chan hien
      // bang dieu khien. Trong luc dang sua CUNG mot o, nguoi dung van dong bang
      // lai duoc (openScope khong chay lai cho o dang mo - xem cua chan dau ham).
      store.setInspectorOpen(true);
    },
    [writeCapable, editing, draft],
  );

  const handleRequestEdit = useCallback(
    (block: TextBlock, scope: EditScope) => openScope(block, scope),
    [openScope],
  );

  /**
   * Muc tieu cua lan chay thu.
   *
   * Khi dang soan: van ban nguoi dung dang go. Khi chi dang chon mot khoi:
   * chinh van ban HIEN CO cua khoi - do la phep thu "khoi nay co go lai duoc
   * khong", va no tra loi dung cau hoi ma bang chan doan can tra loi.
   */
  const selectedScope = state.selectedScope;
  const previewScopeId = editing?.scope.id ?? selectedScope?.id ?? null;
  const previewText = editing
    ? draft
    : selectedScope
      ? (state.editedText.get(selectedScope.id) ?? selectedScope.text)
      : '';
  // Can le chi co nghia khi dang soan. Khi chi dang CHON mot khoi, phep chay thu
  // la cau hoi "khoi nay go lai duoc khong" - de loi tu suy ra can le.
  const previewAlign = editing ? (align ?? undefined) : undefined;
  // Kieu chu cung chi co nghia khi dang soan, va cung ly do: khi chi dang CHON
  // mot khoi, phep chay thu la cau hoi "khoi nay go lai duoc khong" tren chinh
  // mat chu cua no.
  const preview = useTextEditPreview(core, state.doc, previewScopeId, previewText, {
    ...(previewAlign ? { align: previewAlign } : {}),
    ...(editing && bold ? { bold: true } : {}),
    ...(editing && italic ? { italic: true } : {}),
    // Phong chu chi co nghia khi dang soan: khi chi CHON mot khoi, phep chay thu
    // hoi "khoi nay go lai duoc khong" tren chinh mat chu cua no.
    ...(editing ? { family } : {}),
    // Co chu chi co nghia khi dang soan VA nguoi dung da chon mot co khac.
    ...(editing && fontSize !== null ? { fontSizePt: fontSize } : {}),
  });
  const alignSupported = coreAcceptsAlign(core);

  const handleCommitEdit = useCallback(() => {
    if (!editing) return;
    const report = preview.report;
    // Cua chan cuoi cung o tang giao dien. Nut da bi khoa khi khong ok, nhung
    // duong ban phim (Enter) khong duoc phep di vong qua no.
    if (!report || !report.ok) {
      store.notify('Chưa áp dụng được — xem lý do trong bảng Thông tin.', 'warn');
      return;
    }
    // Bao cao phai duoc tinh CHO chinh chuoi dang co. Neu khong khop thi day la
    // ket qua cua ban nhap TRUOC DO, va ap dung theo no la ap dung mot phan
    // quyet khong thuoc ve van ban nay.
    // ...VA cho dung can le do. Bao cao chi co gia tri cho CAP (van ban, can le)
    // ma no duoc tinh: doi can le khong lam doi so dong nhung lam doi TOA DO X
    // cua tung dong, va toa do x la thu duong ghi lay thang tu bao cao. Bo qua
    // ve nay thi mot cu bam ngay sau khi doi can le se ghi nhan lenh dua tren
    // ket qua chay thu cua can le TRUOC DO.
    // ...VA cho dung mat chu do. Font dam RONG HON font thuong: mot doan vua
    // khoi khi de thuong co the khong vua khi in dam, va cua chan "khong vua
    // khoi" chi co gia tri neu no chay tren dung mat chu se duoc ghi ra.
    // ...VA cho dung PHONG CHU do. Moi ho font co so do rieng: mot doan vua khoi
    // o Noto Sans co the tran ra o Noto Serif. Xac nhan bang bao cao cua phong A
    // sau khi da doi sang phong B la ghi ra mot dong nam ngoai khoi (bai hoc so 4).
    // ...VA cho dung CO CHU do. Co chu doi so dong va toa do dat chu.
    if (
      preview.forText !== draft ||
      preview.forAlign !== align ||
      preview.forBold !== bold ||
      preview.forItalic !== italic ||
      preview.forFamily !== family ||
      preview.forSize !== fontSize
    ) {
      store.notify('Đang kiểm tra lại — thử lại sau một nhịp.', 'warn');
      return;
    }
    // KHONG co thay doi nao (van ban, can le, dam, nghieng, phong, co chu deu nhu
    // ban dau): dong o soan, khong ghi mot lenh rong. Chi co thay doi kieu chu ma
    // giu nguyen van ban thi VAN ghi - do la ca diem cua viec cho ap dung thay doi
    // chi ve kieu chu.
    const changed =
      draft !== editing.originalText ||
      align !== null ||
      bold ||
      italic ||
      family !== DEFAULT_FONT_FAMILY_ID ||
      fontSize !== null;
    if (!changed) {
      closeEditor();
      return;
    }

    // Truong `blockId` cua hop dong kieu nhan MA PHAM VI. Voi pham vi la ca
    // khoi day dung la ma khoi; voi pham vi hep hon no la "p0-b3#r5". Lenh van
    // tro toi dung mot doi tuong van ban - xem core/edit/scope.ts.
    // `align` chi duoc gan khi nguoi dung DA CHON tuong minh. Bo trong nghia la
    // "theo tai lieu", va duong ghi se suy ra lai tu chu cu - dung hanh vi cu.
    // Gan bua report.align vao day se bien mot gia tri SUY RA thanh mot lua chon
    // dong cung trong lich su lenh: sua lai o do lan sau se keo theo can le do
    // ke ca khi phep suy ra da co cau tra loi tot hon.
    const command: Extract<EditCommand, { kind: 'text.replace' }> = {
      kind: 'text.replace',
      blockId: editing.scope.id,
      newText: draft,
      previousText: editing.originalText,
      ...(align ? { align } : {}),
      ...(bold ? { bold: true } : {}),
      ...(italic ? { italic: true } : {}),
      // Phong chu chi ghi khi KHAC mac dinh, y nhu dam / nghieng: mot lenh cu
      // khong mang truong nay van doc lai duoc, va duong ghi tu dung noto-sans.
      ...(family !== DEFAULT_FONT_FAMILY_ID ? { fontFamily: family } : {}),
      // Co chu chi ghi khi nguoi dung DA CHON tuong minh (khac null): bo trong =
      // giu co chu do duoc tu chu cu, dung hanh vi cu, va lenh cu van doc lai duoc.
      ...(fontSize !== null ? { fontSizePt: fontSize } : {}),
    };
    store.dispatch(command);

    if (report.fontSizePt !== null) {
      // KHOA HINT THEO NOI DUNG LENH, khong theo scope.id. Neu khoa theo scope.id
      // thi mot o sua HAI lan roi Ctrl+Z se de lai hint cua lan SAU (da hoan tac)
      // trong khi lop ve da lui ve chu cua lan TRUOC - ban xem truoc ve chu cu
      // trong hop che / co chu cua lan sau, mot kieu "ban xem truoc noi doi" (do
      // review doi khang bat duoc). Khoa theo hintKey(command) thi undo / redo /
      // gop lenh deu tu dung: activeHints tra hint DUNG bang chinh noi dung cua
      // lenh dang co hieu luc. Xem hintKey() trong viewer/editedItems.ts.
      const key = hintKey(command);
      setHintCache((prev) => {
        const next = new Map(prev);
        next.set(key, {
          fontSizePt: report.fontSizePt as number,
          lineCount: Math.max(1, report.lineCount),
          // Can le LOI DA DUNG. Lop ve khong duoc tu suy ra lan nua - xem ghi chu
          // trong EditRenderHint.
          align: report.align,
          // Hop che do LOI tinh. Ban xem truoc phai ve dung hop nay, khong duoc
          // tu dung bbox: chu cu co dau tieng Viet thi hop che cao hon bbox.
          coverRect: report.coverRect,
        });
        return next;
      });
    }

    store.notify(
      report.substituted
        ? `Đã sửa. Chữ mới dùng font thay thế (${getFontFamily(family).label}) — hình thức tài liệu đã thay đổi.`
        : 'Đã sửa. Bấm Lưu để xuất tệp PDF.',
      report.substituted ? 'warn' : 'info',
    );
    closeEditor();
  }, [
    editing,
    draft,
    fontSize,
    align,
    bold,
    italic,
    family,
    preview.report,
    preview.forText,
    preview.forAlign,
    preview.forBold,
    preview.forItalic,
    preview.forFamily,
    preview.forSize,
    closeEditor,
  ]);

  // Doi cong cu khac thi dong trinh soan: giu no mo se de nguoi dung go vao
  // mot o ma thao tac hien hanh khong con lien quan gi den no.
  useEffect(() => {
    if (state.tool !== 'text' && editing) closeEditor();
  }, [state.tool, editing, closeEditor]);

  // Doi cong cu cung dong o chu giai dang mo, va vi cung mot ly do: mot o soan
  // con mo trong khi cong cu da doi la mot cai bay - go xong bam Lưu thi khong
  // ro no gan vao dau.
  useEffect(() => {
    const textTool = state.tool === 'note' || state.tool === 'freetext';
    if (!textTool && noteDraft && noteDraft.id === null) setNoteDraft(null);
  }, [state.tool, noteDraft]);

  /**
   * MOT THAO TAC TRANG CO THE RUT MAT TRANG DANG SOAN.
   *
   * Xoa trang 3 trong khi o soan dang mo tren trang 3 se de lai mot o soan neo
   * vao mot trang khong con ton tai: go tiep van duoc, bam Áp dụng van duoc, va
   * lenh sinh ra tro toi mot khoi ma tep xuat ra khong co. Nguoi dung mat cong
   * ma khong he duoc bao.
   *
   * Trang chi DOI CHO thi khong dong: o soan duoc ve theo o trang mang trang
   * goc do, nen no tu di theo sang vi tri moi - khong can lam gi.
   */
  useEffect(() => {
    if (!editing) return;
    if (planContainsOriginalPage(state.pagePlan, editing.pageIndex)) return;
    closeEditor();
    store.notify(
      `Đã đóng ô soạn: trang ${editing.pageIndex + 1} không còn trong tài liệu. ` +
        'Hoàn tác (Ctrl+Z) sẽ mang trang đó trở lại.',
      'warn',
    );
  }, [state.pagePlan, editing, closeEditor]);

  // O chu giai cung vay - va voi ghi chu thi con ro hon: dau ghi chu tren mot
  // trang da xoa se bi loai o buoc luu (xem placeAnnotations).
  useEffect(() => {
    if (!noteDraft) return;
    if (planContainsOriginalPage(state.pagePlan, noteDraft.pageIndex)) return;
    setNoteDraft(null);
  }, [state.pagePlan, noteDraft]);

  // So do ngan di thi trang hien hanh co the tro ra ngoai. Bo dem tren thanh
  // cong cu doc thang gia tri nay, nen de no lech la hien "5 / 4".
  useEffect(() => {
    const count = state.pagePlan.length;
    if (count === 0) return;
    if (state.currentPage > count - 1) store.setCurrentPage(count - 1);
  }, [state.pagePlan.length, state.currentPage]);

  const editSession: EditSession | null = useMemo(() => {
    if (!editing) return null;
    const { block, scope } = editing;

    return {
      scopeId: scope.id,
      parentBlockId: block.id,
      scopeKind: scope.kind,
      scopeIndex: scope.index,
      scopeSiblingCount: scope.siblingCount,
      pageIndex: editing.pageIndex,
      originalText: editing.originalText,
      fontCount: editing.fontCount,
      draft,
      align,
      alignSupported,
      onAlignChange: setAlign,
      bold,
      italic,
      onBoldChange: setBold,
      onItalicChange: setItalic,
      fontFamily: family,
      onFontFamilyChange: setFamily,
      fontSizePt: fontSize,
      // Co chu do duoc tu chu cu - gia tri hien trong o chon co chu khi nguoi
      // dung chua chon gi (fontSizePt null).
      baseFontSizePt: runsFontSizePt(scope.runs) || DEFAULT_TEXT_SIZE_PT,
      onFontSizeChange: setFontSize,
      report: preview.report,
      reportForText: preview.forText,
      reportForAlign: preview.forAlign,
      reportForBold: preview.forBold,
      reportForItalic: preview.forItalic,
      reportForFamily: preview.forFamily,
      reportForSize: preview.forSize,
      pending: preview.pending,
      unsupported: preview.unsupported,
      onDraftChange: setDraft,
      onCommit: handleCommitEdit,
      onCancel: closeEditor,
      // Focus vao o go = mo bang Thong tin (nguoi dung yeu cau). Bo sung cho
      // openScope: neu ho da dong bang roi bam LAI vao o soan dang mo, focus se
      // dua bang len lai. setInspectorOpen bo qua khi da mo nen khong lap.
      onFocus: () => store.setInspectorOpen(true),
    };
  }, [editing, draft, align, bold, italic, family, fontSize, alignSupported, preview, handleCommitEdit, closeEditor]);

  // --- Anh co san trong tai lieu -------------------------------------------

  /**
   * Danh sach lenh DANG CO HIEU LUC.
   *
   * Vi tri anh tren man hinh duoc SUY RA tu day moi lan ve (projectImages), y
   * het `editedText` va `pagePlan`. Khong co ban do vi tri thu hai - do la thu
   * lam cho Ctrl+Z tu dong dung: cat bot danh sach lenh thi anh tu ve cho cu.
   */
  const activeCommands = useMemo(
    () => state.history.slice(0, state.historyIndex).map((entry) => entry.record.command),
    [state.history, state.historyIndex],
  );

  /**
   * LAN SUA CHU DANG CO HIEU LUC, cho lop chinh sua ve de len man hinh.
   *
   * Suy ra tu activeCommands (NGUON CHAN LY) chu khong tu `state.editedText`: ban
   * do editedText dat `blockId -> newText` va GIU LAI ke ca sau Ctrl+Z, nen mot
   * lan sua chi doi phong / dam / nghieng (chu giu nguyen) khong phan biet duoc
   * voi mot lan da hoan tac - ca hai deu co chu = chu goc. Danh sach lenh thi sau
   * undo khong con lenh do. Nho the lop ve moi hien duoc lan doi-chi-kieu-chu ngay
   * tren man hinh (truoc day phai bam Luu xuat tep moi thay). Lenh cuoi cung cho
   * moi blockId thang - mot o sua nhieu lan chi ve trang thai moi nhat.
   */
  const editedRuns = useMemo(() => editedItemsFromCommands(activeCommands), [activeCommands]);

  /**
   * SO DO DAT CHU cho tung o DANG CO HIEU LUC, khoa lai theo scope.id cho lop ve.
   *
   * Doi chieu tu bo nho dem `hintCache` (khoa theo NOI DUNG lenh) sang tung o dang
   * co hieu luc: voi moi lenh text.replace con trong danh sach, tra hint da tinh
   * DUNG cho noi dung cua chinh no. Nho the sau Ctrl+Z - luc lop ve lui ve chu cua
   * lan sua TRUOC - hint cung lui theo, khong con ke thua hop che / co chu cua lan
   * sua sau (loi "ban xem truoc noi doi" do review doi khang bat duoc). Neu chi
   * ghi de mot ban do theo scope.id nhu truoc thi undo khong bao gio dat lai hint.
   */
  const activeHints = useMemo(() => {
    const map = new Map<string, EditRenderHint>();
    for (const command of activeCommands) {
      if (command.kind !== 'text.replace') continue;
      const hint = hintCache.get(hintKey(command));
      if (hint) map.set(command.blockId, hint);
    }
    return map;
  }, [activeCommands, hintCache]);

  /**
   * KEO XONG MOT ANH -> mot lenh content.image.transform.
   *
   * Lenh mang MA TRAN da tinh xong chu khong mang hop chu nhat: doi tu hop chu
   * nhat tren trang sang ma tran `cm` cuc bo can biet phan bien doi bao ngoai,
   * va cho nay la noi duy nhat con nhin thay no. Bat duong ghi lam lai phep doi
   * do bang mot nhanh ma thu hai la mo duong cho hai nhanh troi khoi nhau - xem
   * core/content/projection.ts.
   */
  const handleEmbeddedImageRect = useCallback((image: ProjectedImage, rect: PdfRect) => {
    const local = image.currentLocal;
    const full = currentFullMatrix(image);
    if (!local || !full) {
      store.notify(
        'Không xác định được đoạn nào trong luồng nội dung đặt ảnh này, nên không di chuyển ' +
          'được. Cần lõi Rust/PDFium.',
        'warn',
      );
      return;
    }
    const matrix = localMatrixForRect(local, full, rect);
    if (!matrix) {
      store.notify('Kích thước mới của ảnh không hợp lệ (bề rộng hoặc chiều cao bằng 0).', 'warn');
      return;
    }
    store.dispatch({
      kind: 'content.image.transform',
      pageIndex: image.pageIndex,
      occurrence: image.occurrence,
      matrix,
      previousMatrix: local,
    });
  }, []);

  const handleDeleteImage = useCallback(() => {
    const id = selectedImageId;
    if (!id) return;
    const m = /^p(\d+)-img(\d+)$/.exec(id);
    if (!m) return;
    store.dispatch({
      kind: 'content.image.remove',
      pageIndex: Number(m[1]),
      occurrence: Number(m[2]),
    });
    setSelectedImageId(null);
    store.notify(
      'Đã xóa ảnh khỏi nội dung trang. Khác với che chữ, đây là xóa THẬT: khối lệnh vẽ ảnh ' +
        'bị cắt khỏi luồng nội dung khi lưu.',
      'info',
    );
  }, [selectedImageId]);

  const embeddedImageBridge: EmbeddedImageBridge = useMemo(
    () => ({
      commands: activeCommands,
      selectedId: selectedImageId,
      onSelect: (image) =>
        setSelectedImageId(image ? `p${image.pageIndex}-img${image.occurrence}` : null),
      onCommitRect: handleEmbeddedImageRect,
      // Do hoa vecto: noi that, mot lan, va khong bay ra mot cong cu khong chay.
      onVectorNote: (note) => store.notify(note, 'warn'),
      onRefusal: (reason) => store.notify(reason, 'warn'),
    }),
    [activeCommands, selectedImageId, handleEmbeddedImageRect],
  );

  // --- Luu ----------------------------------------------------------------

  /**
   * DUNG TEP PDF HIEN HANH tu byte goc + toan bo danh sach lenh dang co hieu luc.
   *
   * DUONG DUY NHAT sinh ra byte PDF trong ung dung. Ca ba noi can byte - bam
   * Luu, trich xuat trang da chon, tach tung trang - deu goi ham nay. Neu moi
   * noi tu dung tep theo cach rieng, chung se troi khoi nhau, va tep nguoi dung
   * trich ra se khong khop voi tep ho luu.
   *
   * Moi lan goi deu mo mot tai lieu lam viec MOI tu byte goc roi phat lai ca
   * danh sach lenh len do. Tai lieu dang hien thi khong bao gio bi ghi vao, nen
   * goi ham nay hai lan cho ra hai luong byte GIONG HET NHAU - lenh khong bao
   * gio bi ap chong len nhau.
   */
  const buildCurrentPdf = useCallback(async (): Promise<Uint8Array | null> => {
    const original = originalBytesRef.current;
    if (!core || !original) return null;

    const commands = store.activeCommands();
    // Duong ghi cua ban web: sua chu + thao tac trang + CHU THICH + PHAU THUAT
    // ANH trong content stream. Chi che thong tin la con phai cho loi
    // Rust/PDFium.
    //
    // Lenh anh PHAI co trong danh sach nay. Loi chi phau thuat anh o giai doan
    // 0 cua save() dua tren `state.commands`, ma `state.commands` chi day len
    // qua core.apply() - tuc la qua chinh vong lap ben duoi. Loc lenh anh ra o
    // day khong lam no bi "bo qua mot cach an toan": no lam cu keo anh bien mat
    // hoan toan khoi tep xuat ra trong khi man hinh van hien anh o vi tri moi.
    const writable = commands.filter(
      (c) =>
        c.kind === 'text.replace' ||
        isPageCommand(c) ||
        isAnnotationCommand(c) ||
        isImageContentCommand(c),
    );
    const skipped = commands.length - writable.length;

    // KHONG BAO GIO am tham bo qua mot lenh. Neu ban web khong ghi duoc mot
    // thao tac, nguoi dung phai biet TRUOC khi tep roi khoi may - khong phai
    // phat hien ra khi mo lai va thay thao tac cua minh bien mat.
    if (skipped > 0) {
      const proceed = window.confirm(
        `Bản web ghi được các lần sửa chữ, các thao tác trang và chú thích.\n\n` +
          `${skipped} thao tác khác sẽ KHÔNG có trong tệp xuất ra. ` +
          `Chúng vẫn còn trong lịch sử trên màn hình.\n\nVẫn xuất tệp?`,
      );
      if (!proceed) return null;
    }

    // Chu thich nam tren mot trang DA BI XOA se khong co cho trong tep xuat ra.
    // Do la hanh vi dung (khong con trang de bam vao), nhung no van la mat du
    // lieu so voi nhung gi nguoi dung tung dat vao - nen phai noi ra TRUOC.
    const { dropped } = placeAnnotations(store.getSnapshot().annotations, store.getSnapshot().pagePlan);
    if (dropped.length > 0) {
      const proceed = window.confirm(
        `${dropped.length} chú thích nằm trên trang đã bị xóa.\n\n` +
          `Chúng sẽ KHÔNG có trong tệp xuất ra. Hoàn tác thao tác xóa trang thì chúng quay lại.` +
          `\n\nVẫn xuất tệp?`,
      );
      if (!proceed) return null;
    }

    // Mo mot ban lam viec RIENG tu byte goc.
    const scratch = await core.open(original.slice());
    if (!scratch.ok) {
      store.notify(scratch.error.userMessage, 'error');
      return null;
    }
    const target = scratch.value;

    try {
      // Cac lenh chi duoc vat chat hoa TAI DAY. Truoc thoi diem nay, tep goc
      // van nguyen ven tung byte.
      for (const command of writable) {
        const applied = await core.apply(target, command);
        if (!applied.ok) {
          store.notify(applied.error.userMessage, 'error');
          return null;
        }
      }

      // GHI TANG TRUONG LA CAM, KHONG PHAI MAC DINH CO THE DOI.
      // Luu tang truong noi them ban sua doi va giu lai phien ban object cu.
      // Voi tai lieu da che thong tin, dieu do lam noi dung "da che" lay lai
      // duoc bang mot trinh xem bat ky - mau ro ri du lieu da xay ra nhieu lan
      // trong thuc te. O day khong co giao dien nao bat duoc no len true.
      const saved = await core.save(target, { incremental: false });
      if (!saved.ok) {
        store.notify(saved.error.userMessage, 'error');
        return null;
      }
      return saved.value;
    } catch (err) {
      store.notify(`Không xuất được tệp: ${String(err).slice(0, 160)}`, 'error');
      return null;
    } finally {
      await core.close(target);
    }
  }, [core]);

  const handleSave = useCallback(async () => {
    const snapshot = store.getSnapshot();
    if (!core || !snapshot.doc || !originalBytesRef.current || saving) return;

    setSaving(true);
    try {
      const bytes = await withTimeout(
        buildCurrentPdf(),
        SAVE_TIMEOUT_MS,
        'Xuất tệp quá lâu và đã bị dừng.',
      );
      if (!bytes) {
        // buildCurrentPdf tra ve null khi khong dung duoc byte goc hoac loi tu
        // choi mot lenh. Truoc day ta return lang le va nut chi don gian het
        // "Đang xuất..." - nguoi dung khong biet la KHONG CO TEP NAO duoc tao.
        store.notify(
          'Không dựng được tệp PDF. Hãy thử hoàn tác thao tác gần nhất rồi xuất lại.',
          'warn',
        );
        return;
      }

      downloadPdfBytes(bytes, `${baseNameOf(snapshot.fileName)}-da-sua.pdf`);

      const commands = store.activeCommands();
      const textEdits = commands.filter((c) => c.kind === 'text.replace').length;
      const pageOps = commands.filter(isPageCommand).length;
      // Dem CHU THICH DANG CO, khong phai so lenh: ba lenh (them, sua, xoa) tren
      // cung mot dau cho ra khong dau nao trong tep, va bao "3 chú thích" luc do
      // la bao sai.
      const annotationCount = placeAnnotations(
        store.getSnapshot().annotations,
        store.getSnapshot().pagePlan,
      ).placements.length;
      // Dem anh theo so ANH BI DONG VAO, khong phai so lenh: mot cu keo chuot
      // sinh ra hang chuc lenh transform ma collapseImageEdits() gop lai thanh
      // mot, nen dem lenh se bao "37 thao tác ảnh" cho mot lan keo.
      const imageOps = collapseImageEdits(commands.filter(isImageContentCommand)).size;
      const parts: string[] = [];
      if (textEdits > 0) parts.push(`${textEdits} lần sửa chữ`);
      if (pageOps > 0) parts.push(`${pageOps} thao tác trang`);
      if (annotationCount > 0) parts.push(`${annotationCount} chú thích`);
      if (imageOps > 0) parts.push(`thay đổi ảnh trên ${imageOps} trang`);
      store.notify(
        parts.length === 0
          ? 'Đã xuất tệp PDF (không có thay đổi nào).'
          : `Đã xuất tệp PDF với ${parts.join(' và ')}.`,
        'info',
      );
    } catch (err) {
      // KHONG duoc nuot loi o day. Day la duong duy nhat tao ra TEP cua nguoi
      // dung: mot loi im lang o day nghia la ho bam Luu, khong thay gi xay ra,
      // va khong biet minh vua mat cong hay chua.
      store.notify(
        `Xuất tệp thất bại: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    } finally {
      setSaving(false);
    }
  }, [core, saving, buildCurrentPdf]);

  // --- Thao tac trang -------------------------------------------------------

  /**
   * MOT ban trang thai trang cho CA HAI khung hinh.
   *
   * Thanh cong cu tren mang "Xoay trái / Xoay phải / Xóa trang" va menu "Trang";
   * thanh ben mang anh thu nho va viec CHON. Ca hai deu chi la cach ve ra cung
   * mot hook nay. Neu moi ben tu giu lua chon rieng thi nut Xóa tren thanh cong
   * cu se xoa mot tap trang khac voi tap dang sang len o thanh ben.
   */
  const pageOps = usePageOps({
    core,
    doc: state.doc,
    plan: state.pagePlan,
    fileName: state.fileName,
    buildCurrentPdf,
  });

  // --- Phim tat -----------------------------------------------------------

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        store.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void handleSave();
        return;
      }
      if (mod && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        store.zoomIn();
        return;
      }
      if (mod && e.key === '-') {
        e.preventDefault();
        store.zoomOut();
        return;
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        store.setZoom(1);
        return;
      }
      // CAN LE CUA DOI TUONG DANG CHON - Ctrl+Shift+L / E / R.
      //
      // DUNG bo phim ma trinh soan tai cho da dung (InlineTextEditor). Hai duong
      // soan thao khac nhau trong cung mot ung dung ma doi hoi hai bo phim can
      // le khac nhau la mot cai bay: nguoi dung hoc mot lan roi go nham mai mai.
      if (mod && e.shiftKey && !e.altKey) {
        const next = ALIGN_KEYS[e.key.toLowerCase()];
        if (next && store.getSnapshot().selectedAnnotationId) {
          e.preventDefault();
          patchAnnotation({ textAlign: next });
          return;
        }
      }
      if (e.key === 'Escape') {
        // BANG THONG TIN DONG TRUOC MOI VIEC KHAC.
        //
        // Esc phai go bo dung mot lop, tu tren xuong. Gop chung lai se lam mot
        // cu Esc de dong bang cung xoa luon lua chon phia duoi no, va nguoi dung
        // mat cai ho dang xem ma khong he yeu cau.
        if (store.getSnapshot().inspectorOpen) {
          store.setInspectorOpen(false);
          return;
        }
        closeEditor();
        setNoteDraft(null);
        store.selectBlock(null, null);
        store.selectAnnotation(null);
        return;
      }
      // Xoa doi tuong dang chon. Chi khi con tro KHONG o trong o soan - dong
      // chan dau ham nay da lo cho do.
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // ANH CO SAN xet TRUOC chu thich: no la thu vua duoc chon gan nhat khi
        // ca hai cung co gia tri, va mot phim Delete xoa mat mot doi tuong khac
        // voi cai dang co o chon quanh no la kieu hong khong ai tha thu.
        if (selectedImageId) {
          e.preventDefault();
          handleDeleteImage();
          return;
        }
        const id = store.getSnapshot().selectedAnnotationId;
        if (id) {
          e.preventDefault();
          deleteAnnotation(id);
          return;
        }
      }
      const key = e.key.toLowerCase();
      /*
        PHIM TAT CONG CU DOC TU BANG CHUNG voi thanh cong cu (ui/toolbarMenus.ts).

        Truoc day day la mot day `case 'r': store.setTool('square')`. No chay
        dung, nhung no la BAN SAO THU HAI cua danh sach cong cu: dua "Hình chữ
        nhật" vao menu "Chèn" ma quen dong `case 'r':` thi phim tat chet lang le,
        va them mot cong cu vao menu ma quen bang nay thi muc menu quang cao mot
        phim khong lam gi. Doc tu mot bang thi hai truong hop do khong ton tai
        duoc nua, va co test doi chieu hai chieu bang do voi cac muc menu.
      */
      const tool = TOOL_SHORTCUTS[key];
      if (tool) {
        store.setTool(tool);
        return;
      }
      // 'i' KHONG phai mot cong cu: no mo bang thong tin. De no o day cung cho
      // voi cac phim cong cu vi day la noi nguoi dung tim phim tat, va de no di
      // cho khac se sinh ra hai bang phim tat phai giu dong bo.
      //
      // 'h' KHONG con gan voi gi ca: cong cu "Tô sáng" da bi go bo. De lai phim
      // tat cho mot cong cu khong con nut bam la de lai mot duong am tham vao
      // mot trang thai khong hien duoc tren thanh cong cu.
      if (key === 'i') store.toggleInspector();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    closeEditor,
    handleSave,
    patchAnnotation,
    deleteAnnotation,
    selectedImageId,
    handleDeleteImage,
  ]);

  const selectedBlockId = state.selectedBlock?.id ?? null;

  const textEditCount = useMemo(
    () => store.activeCommands().filter((c) => c.kind === 'text.replace').length,
    // historyIndex/history la thu that su quyet dinh danh sach lenh dang co
    // hieu luc; store.activeCommands() chi la ham doc tren chung.
    [state.history, state.historyIndex],
  );

  /**
   * ==========================================================================
   *  BANG THUOC TINH cho thu dang sua / dang chon.
   * ==========================================================================
   *  MOT bang cho ca ba ngu canh - sua chu san co, hop chu / ghi chu, hinh khoi.
   *  Cai gi hai duong khac nhau THAT (ket qua chay thu + canh bao thay font, hay
   *  mau nen + do mo) duoc the hien bang cac phan cam vao, khong bang mot bang
   *  thu hai. Xem ui/PropertiesPanel.tsx.
   */
  const objectExtras = (a: Annotation): Pick<PropertiesPanelProps, 'fill' | 'opacity' | 'destructive'> => {
    const fill = a.fillRgb ?? null;
    return {
      fill: {
        hex: hexColor(fill ?? [1, 1, 1]),
        enabled: fill !== null,
        onChange: (hex) => patchAnnotation({ fillRgb: rgbFromHex(hex) }),
        // Bo to <-> to lai bang mau vien, y het PropertyBar cu.
        onToggle: () => patchAnnotation({ fillRgb: fill === null ? [...a.colorRgb] : null }),
      },
      opacity: { value: a.opacity, onChange: (v) => patchAnnotation({ opacity: v }) },
      destructive: {
        label: 'Xóa',
        title: 'Xóa hẳn đối tượng này khỏi tài liệu',
        onClick: () => deleteAnnotation(a.id),
      },
    };
  };

  let propertiesPanel: ReactElement | null = null;
  if (editSession) {
    propertiesPanel = (
      <PropertiesPanel
        title="Sửa chữ"
        style={{
          fontFamily: {
            value: editSession.fontFamily,
            onChange: editSession.onFontFamilyChange,
          },
          fontSize: {
            value: editSession.fontSizePt ?? editSession.baseFontSizePt,
            onChange: editSession.onFontSizeChange,
          },
          bold: editSession.bold,
          italic: editSession.italic,
          onBold: editSession.onBoldChange,
          onItalic: editSession.onItalicChange,
          boldTitle:
            'Đậm — chữ mới sẽ dùng mặt chữ đậm thật của phông đóng gói, không phải font gốc của tài liệu',
          italicTitle:
            'Nghiêng — chữ mới sẽ dùng mặt chữ nghiêng thật của phông đóng gói, không phải font gốc của tài liệu',
          align: editSession.align ?? editSession.report?.alignDetected ?? null,
          onAlign: editSession.onAlignChange,
          alignSupported: editSession.alignSupported,
          alignDisabledTitle: 'Lõi đang chạy chưa nhận tùy chọn căn lề.',
          note: <EditFeedback session={editSession} />,
        }}
      />
    );
  } else if (noteDraft) {
    const existing = noteDraft.id !== null ? selectedAnnotation : null;
    propertiesPanel = (
      <PropertiesPanel
        title={noteDraft.kind === 'free-text' ? 'Hộp chữ' : 'Ghi chú'}
        style={{
          fontFamily: { value: noteStyle.fontFamily, onChange: (id) => patchNoteStyle({ fontFamily: id }) },
          fontSize: { value: noteStyle.fontSizePt, onChange: (pt) => patchNoteStyle({ fontSizePt: pt }) },
          bold: noteStyle.bold,
          italic: noteStyle.italic,
          onBold: (b) => patchNoteStyle({ bold: b }),
          onItalic: (b) => patchNoteStyle({ italic: b }),
          boldTitle: 'Đậm — dùng mặt chữ đậm thật của phông đang chọn',
          italicTitle: 'Nghiêng — dùng mặt chữ nghiêng thật của phông đang chọn',
          align: noteStyle.align,
          onAlign: (a) => patchNoteStyle({ align: a }),
          alignSupported: true,
          color: {
            hex: hexColor(noteStyle.textColorRgb),
            onChange: (hex) => patchNoteStyle({ textColorRgb: rgbFromHex(hex) }),
          },
        }}
        // Mau nen / do mo chi hien khi doi tuong DA TON TAI: hop chu chua luu thi
        // chua co gi de doi nen. Nut Xóa dung handleNoteDelete (dong ca o soan),
        // khong dung deleteAnnotation truc tiep - de lai o soan tro toi mot doi
        // tuong da bien mat.
        {...(existing
          ? {
              ...objectExtras(existing),
              destructive: {
                label: 'Xóa',
                title: 'Xóa hẳn đối tượng này khỏi tài liệu',
                onClick: handleNoteDelete,
              },
            }
          : {})}
      />
    );
  } else if (selectedAnnotation) {
    const isText = selectedAnnotation.kind === 'free-text' || selectedAnnotation.kind === 'text-note';
    // Doi tuong co chu duoc chon nhung o soan chua mo (hiem): van cho doi kieu
    // chu qua patchAnnotation. Hinh khoi thi khong co phan kieu chu.
    propertiesPanel = (
      <PropertiesPanel
        title={KIND_LABEL[selectedAnnotation.kind] ?? selectedAnnotation.kind}
        {...(isText
          ? {
              style: {
                fontFamily: {
                  value: selectedAnnotation.fontFamily,
                  onChange: (id) => patchAnnotation({ fontFamily: id }),
                },
                fontSize: {
                  value: selectedAnnotation.fontSizePt ?? DEFAULT_TEXT_SIZE_PT,
                  onChange: (pt) => patchAnnotation({ fontSizePt: pt }),
                },
                bold: selectedAnnotation.bold === true,
                italic: selectedAnnotation.italic === true,
                onBold: (b) => patchAnnotation({ bold: b }),
                onItalic: (b) => patchAnnotation({ italic: b }),
                align: selectedAnnotation.textAlign ?? 'left',
                onAlign: (a) => patchAnnotation({ textAlign: a }),
                alignSupported: true,
                color: {
                  hex: hexColor(selectedAnnotation.textColorRgb ?? DEFAULT_TEXT_COLOR_RGB),
                  onChange: (hex) => patchAnnotation({ textColorRgb: rgbFromHex(hex) }),
                },
              } as const,
            }
          : {})}
        {...objectExtras(selectedAnnotation)}
      />
    );
  }

  // AUTO-MO bang Thong tin khi bat dau sua / chon mot thu moi: nguoi dung khong
  // bao gio phai di tim bo dieu khien. Chi mo khi MUC TIEU doi sang mot cai moi -
  // nen dong bang lai trong luc van dang sua cung mot o thi no khong tu bat lai.
  const propTarget =
    editing?.scope.id ??
    (noteDraft ? `note:${noteDraft.id ?? 'new'}:${noteDraft.pageIndex}` : null) ??
    state.selectedAnnotationId ??
    null;
  const prevPropTargetRef = useRef<string | null>(null);
  useEffect(() => {
    if (propTarget && propTarget !== prevPropTargetRef.current) {
      store.setInspectorOpen(true);
    }
    prevPropTargetRef.current = propTarget;
  }, [propTarget]);

  // Bam CONG CU "Sửa chữ" la MO NGAY bang Thong tin - khong doi den luc bam
  // trung mot o. Bo dieu khien phong / co chu / kieu chu nam trong bang do; neu
  // bang con dong thi nguoi dung bam "Sửa chữ" roi chon phong se khong thay cho
  // nao de chon, va tuong tinh nang khong co tac dung. Chi bat khi VUA chuyen
  // sang 'text' de khong ep bang mo lai sau khi nguoi dung tu dong no.
  const prevToolRef = useRef(state.tool);
  useEffect(() => {
    if (state.tool === 'text' && prevToolRef.current !== 'text') {
      store.setInspectorOpen(true);
    }
    prevToolRef.current = state.tool;
  }, [state.tool]);

  return (
    <div className={`app${state.sidebarOpen ? '' : ' sidebarHidden'}`}>
      <Toolbar
        onOpenFile={(file) => void handleOpenFile(file)}
        onGoToPage={goToPage}
        onSave={() => void handleSave()}
        writeCapable={writeCapable}
        saving={saving}
        pendingTextEdits={textEditCount}
        onPickImage={(file) => void handlePickImage(file)}
        imageCapable={asImageCore(core) !== null}
        imageArmed={pendingImage !== null}
        imageLabel={pendingImage?.label ?? null}
        pageOps={pageOps}
      />

      <div className="workspace">
        {state.sidebarOpen && (
          <PagePanel
            core={core}
            doc={state.doc}
            slots={viewSlots}
            documents={planDocuments}
            currentPage={state.currentPage}
            onGoToPage={goToPage}
            fileName={state.fileName}
            ops={pageOps}
          />
        )}

        <main className="viewerArea">
          {/*
            THANH THUOC TINH RIENG (PropertyBar) da bi go bo: moi dieu khien kieu
            chu, mau nen, do mo va nut xoa cua doi tuong dang chon gio nam trong
            bang Thong tin (PropertiesPanel trong InspectorPopover), cung mot cho
            voi dieu khien cua duong sua chu san co. Mot bang duy nhat cho ca hai
            duong - xem ui/PropertiesPanel.tsx.
          */}

          {state.status === 'ready' && state.info && (
            <VirtualizedDocument
              core={core}
              slots={viewSlots}
              documents={planDocuments}
              zoom={state.zoom}
              annotations={state.annotations}
              selectedBlockId={selectedBlockId}
              selectedScope={state.selectedScope}
              onSelectBlock={handleSelectBlock}
              onBlocksLoaded={handleBlocksLoaded}
              onCurrentPageChange={handleCurrentPageChange}
              scrollRequest={scrollRequest}
              tool={state.tool}
              editSession={editSession}
              edited={editedRuns}
              editHints={activeHints}
              onRequestEdit={handleRequestEdit}
              editRevision={state.historyIndex}
              onInkStroke={handleInkStroke}
              onNotePoint={handleNotePoint}
              onShape={handleShape}
              onImageRect={handleImageRect}
              imageUrl={imageUrlFor}
              onSelectAnnotation={handleSelectAnnotation}
              selectedAnnotationId={state.selectedAnnotationId}
              onDeselectAnnotation={() => store.selectAnnotation(null)}
              onChangeAnnotationRect={changeAnnotationRect}
              onDeleteAnnotation={deleteAnnotation}
              noteDraft={noteDraft}
              noteStyle={noteStyle}
              onNoteSave={handleNoteSave}
              onNoteAlignChange={handleNoteAlignChange}
              onNoteCancel={handleNoteCancel}
              embeddedImages={embeddedImageBridge}
            />
          )}

          {state.status === 'loading' && (
            <div className="centerNotice">
              <div className="spinner" aria-hidden="true" />
              <p>Đang mở {state.fileName}…</p>
            </div>
          )}

          {state.status === 'error' && state.error && (
            <div className="centerNotice error">
              <h2>Không mở được tài liệu</h2>
              <p>{state.error.userMessage}</p>
              <code className="errorCode">{state.error.code}</code>
              <button type="button" className="button" onClick={() => store.reset()}>
                Thử tệp khác
              </button>
            </div>
          )}

          <DropZone
            onFile={(file) => void handleOpenFile(file)}
            showEmptyState={state.status === 'empty'}
            coreLabel={state.coreLabel}
          />
        </main>

        {/*
          BANG THONG TIN NOI - thay cho <aside className="rightRail"> cu.

          No nam TRONG .workspace va dinh vi tuyet doi so voi khung nay, nen no
          phu len le phai cua khung xem chu khong chiem mot cot cua luoi. Do la
          dieu lam cho trang thuc su rong ra khi bang dong: .workspace chi con
          hai cot, ResizeObserver trong VirtualizedDocument thay khung xem rong
          hon va tinh lai ti le "vừa rộng" cung nhu be rong canvas.
        */}
        {state.inspectorOpen && (
          <InspectorPopover
            onClose={() => store.setInspectorOpen(false)}
            editing={editing !== null}
            {...(propertiesPanel ? { properties: propertiesPanel } : {})}
            objects={{
              /*
                Trang cua BANG DOI TUONG la chi so trong TAI LIEU GOC, khong phai
                vi tri trong so do trang. Chu thich song trong khong gian chi so
                goc (Annotation.pageIndex), nen loc theo so do se liet ke nham
                trang ngay sau lan sap xep lai dau tien.
              */
              annotations: state.annotations,
              pageIndex: viewSlots[state.currentPage]?.originalPageIndex ?? null,
              selectedId: state.selectedAnnotationId,
              // Bang chi liet ke doi tuong cua TRANG DANG XEM, nen chon mot dong
              // khong bao gio phai cuon di dau ca.
              onSelect: (id) => store.selectAnnotation(id),
              // DUNG duong doi cho cu, khong sao chep lai: reorderAnnotation()
              // phat mot lenh annotation.update doi rieng `z`, va `z` chinh la
              // thu tu ghi vao /Annots. Mot phep sap xep rieng cho bang nay se
              // cho ra mot ban xem truoc noi doi - da tung xay ra.
              onReorder: reorderAnnotation,
              onDelete: deleteAnnotation,
            }}
          />
        )}
      </div>

      <StatusBar />

      {state.toast && (
        <div className={`toast ${state.toast.tone}`} role="status">
          <span>{state.toast.text}</span>
          <button type="button" className="toastClose" onClick={() => store.dismissToast()}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBar() {
  const state = useAppState();
  const info = state.info;
  const planLength = state.pagePlan.length || (info?.pageCount ?? 0);
  const undoCount = state.historyIndex;
  const redoCount = state.history.length - state.historyIndex;

  return (
    <footer className="statusBar">
      <span className={`coreBadge${state.coreFlavor === 'wasm' ? ' live' : ''}`}>
        {state.coreLabel}
      </span>
      {info && (
        <>
          <span className="statusItem">PDF {info.pdfVersion}</span>
          {/*
            So trang cua TAI LIEU DANG CO, tuc la do dai so do trang - khong
            phai so trang cua tep da mo luc dau. Khi hai so khac nhau thi noi ca
            hai: "5 trang (gốc 3)" tra loi ngay cau hoi nguoi dung se hoi.
          */}
          <span className="statusItem">
            {planLength} trang
            {planLength !== info.pageCount ? ` (gốc ${info.pageCount})` : ''}
          </span>
          {info.encrypted && <span className="statusItem warn">Đã mã hóa</span>}
          {info.signed && <span className="statusItem warn">Có chữ ký số</span>}
          {info.tagged && <span className="statusItem">Có gắn thẻ</span>}
          {info.hasXfa && <span className="statusItem warn">XFA — chỉ đọc</span>}
          {info.linearized && <span className="statusItem">Linearized</span>}
        </>
      )}
      <span className="statusSpacer" />
      <span className="statusItem">
        Lịch sử: {undoCount} hoàn tác · {redoCount} làm lại
      </span>
      {state.fileSizeBytes > 0 && (
        <span className="statusItem">{formatBytes(state.fileSizeBytes)}</span>
      )}
      {/* Zoom o goc phai thanh trang thai, kieu Word. Chi hien khi co tai lieu. */}
      {info && (
        <span className="statusZoom">
          <button
            type="button"
            className="iconButton"
            onClick={() => store.zoomOut()}
            title="Thu nhỏ (Ctrl+-)"
            aria-label="Thu nhỏ"
          >
            −
          </button>
          <span className="statusZoomValue">{Math.round(state.zoom * 100)}%</span>
          <button
            type="button"
            className="iconButton"
            onClick={() => store.zoomIn()}
            title="Phóng to (Ctrl++)"
            aria-label="Phóng to"
          >
            +
          </button>
        </span>
      )}
    </footer>
  );
}

// Dat ten tep va tai ve da chuyen sang ui/download.ts: bay gio co ba duong tai
// ve (luu ca tai lieu, trich xuat trang da chon, tach tung trang) va ba ban sao
// cua cung mot doan ma se troi khoi nhau.

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
